<template>
  <div v-if="diaryEntry" class="fill-width">
    <debug :object="diaryEntry" />

    <modal
      name="viewPreviousDiary"
      classes="modalBoon withScroll"
      transition="nice-modal-fade"
      height="80%"
      width="80%"
    >
      <diary-summary :diaryEntry="previousDiaryEntry" :projectCity="currentProject.address.city" />
      <div class="flex-grow-1" />
      <button
        class="negative-button icon-button"
        style="align-self: flex-end"
        @click="$modal.hide('viewPreviousDiary')"
      >
        <b-icon icon="x" :title="$t('Close')" />
      </button>
    </modal>

    <weather
      v-if="currentProject.address"
      :currentdate="currentDate"
      :city="currentProject.address.city"
      class="weather"
    />

    <div class="current-day">
      <div class="current-day-header">
        <span class="mr-3">{{ $t(dayName) }}</span>
        <span class="fw-bold">{{ diaryEntry.diaryDate }}</span>
      </div>

      <div>
        <span class="fw-bold">{{ currentProject.projectNumber }} {{ currentProject.title }}</span>
      </div>

      <div class="current-day-info">
        <span class="mr-3">{{ currentProject.address.street }}</span>
        <span class="mr-3">{{ currentProject.address.zipCode }} {{ currentProject.address.city }}</span>
      </div>
    </div>

    <div class="section-info">
      <img class="diary-logo" src="static/diary-logo.png" alt="Diary Logo" />

      <div class="diary">
        <h2>{{ $t('Enter todays diary') }}</h2>
        <p v-if="copyAvailable">
          {{ $t('Choose to copy the previous entry or start blank') }}
        </p>
        <p v-if="!mayProceed">
          {{ $t('Complement the list of parts below and assign responsibilities.') }}
        </p>
        <p>
          {{ $t('On the next step you will fill out details of each sub contractor.') }}
        </p>
      </div>
    </div>

    <div v-if="!isWorkThisDate" class="section">
      <label for="noWorkPerformed">{{ $t('No Work Performed') }}</label>
      <button id="noWorkPerformed" class="negative-button button pass-button" @click="noWorkPerformed">
        {{ $t('PASS') }}
      </button>
    </div>

    <div v-if="copyAvailable" class="section">
      <label for="copyPrevious">{{ $t('Copy the previous day') }}</label>
      <button @click="showPreviousDiaryEntry">
        <b-icon icon="eye" :title="$t('View the previous entry')" />
      </button>
      <div class="flex-grow-1" />
      <on-off v-model="copyState" id="copyPrevious" />
    </div>

    <div class="section">
      <label for="highlightDate">{{ $t('Highlight This Day') }}</label>
      <on-off :value="diaryEntry.highlightDate" id="highlightDate" @input="updateProperty('highlightDate', $event)" />
    </div>

    <div v-if="diaryEntry.unassigned && diaryEntry.unassigned.length > 0" class="section">
      <div class="d-flex flex-column fill-width">
        <h2>{{ $t('There are unassigned parts') }}</h2>
        <p>
          {{ $t('Complement the list of parts below and assign responsibilities.') }}
        </p>
        <div v-for="itm in diaryEntry.unassigned" :key="itm">
          <diary-part-edit :diaryEntry="diaryEntry" :part="fetchPart(itm)" />
        </div>
      </div>
    </div>

    <div class="section">
      <router-link
        class="negative-button icon-button"
        :title="$t('Close')"
        :to="{ name: 'ProjectOverview', params: { _id: $route.params._id, date: currentDate } }"
      >
        <b-icon icon="x" />
      </router-link>

      <span class="flex-grow-1" />

      <span v-if="!mayProceed">
        {{ $t('Complete the information of all sub contactors to complete the diary') }}
      </span>

      <button v-if="!mayProceed" :title="$t('Edit Entries')" class="negative-button icon-button">
        <b-icon icon="arrow-right-short" />
      </button>

      <router-link
        v-if="mayProceed"
        class="positive-button icon-button"
        :title="$t('Edit Entries')"
        :to="{
          name: 'DiaryEditEntries',
          params: { _id: $route.params._id, date: currentDate },
          query: { copy: copyState },
        }"
      >
        <b-icon icon="arrow-right-short" />
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import Multiselect from 'vue-multiselect';

import Weather from '@/components/diary/Weather';
import OnOff from '@/components/switch/switch';
import DiaryPartEdit from '@/components/diary/DiaryPartEdit';
import DiarySummary from '@/components/diary/DiarySummary';

export default {
  name: 'DiaryEdit',

  components: {
    OnOff,
    Weather,
    Multiselect,
    DiarySummary,
    DiaryPartEdit,
  },

  metaInfo() {
    return {
      title: `${this.$t('Diary')} ${this.currentDate}`,
    };
  },

  data() {
    return {
      diaryEntry: {},
      previousDiaryEntry: {},
      copyState: true,
    };
  },

  computed: {
    ...mapGetters(['currentProject', 'fetchPart']),

    mayProceed() {
      return !this.diaryEntry.unassigned || this.diaryEntry.unassigned.length === 0;
    },

    copyAvailable() {
      return (
        this.diaryEntry.performingCompanies &&
        !this.entryHasData(this.diaryEntry) &&
        this.entryHasData(this.previousDiaryEntry)
      );
    },
    dayName() {
      return moment(this.currentDate).format('dddd');
    },

    currentDate() {
      return this.$route.params.date;
    },

    isWorkThisDate() {
      let day = moment(this.currentDate).day();
      return (
        day > 0 &&
        day < 6 &&
        ((this.diaryEntry.unassigned && this.diaryEntry.unassigned.length !== 0) ||
          (this.diaryEntry.performingCompanies && this.diaryEntry.performingCompanies.length) !== 0)
      );
    },
  },

  methods: {
    ...mapActions(['getDiaryEntryForDate', 'loadProject', 'saveDiaryEntryProperties', 'getPreviousDiaryEntryForDate']),

    updateProperty(property, newValue) {
      if (this.diaryEntry[property] !== newValue) {
        this.saveDiaryEntryProperties({
          diaryEntry: this.diaryEntry,
          [property]: newValue,
        });
      }
    },

    async noWorkPerformed() {
      await this.updateProperty('noWorkPerformed', true);
      await this.updateProperty('signedOff', true);
      this.$router.push({ name: 'GoodJob' });
    },

    entryHasData(entry) {
      return (
        !!(entry && entry.performingCompanies) &&
        entry.performingCompanies.some((company) => {
          return (
            !!(company && company.diaryEntries) &&
            company.diaryEntries.some(({ numberOfWorkers, comment }) => {
              return Number(numberOfWorkers) > 0 || (!!comment && comment.trim().length > 0);
            })
          );
        })
      );
    },

    showPreviousDiaryEntry() {
      this.$modal.show('viewPreviousDiary');
    },
  },

  watch: {
    $route: {
      immediate: true,
      async handler(next) {
        await this.loadProject(next.params._id);
        this.diaryEntry = await this.getDiaryEntryForDate(this.currentDate);
        this.previousDiaryEntry = await this.getPreviousDiaryEntryForDate(this.currentDate);
      },
    },
  },
};
</script>

<style scoped>
.section {
  border-top: 1px solid #253747;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 18px 0;
}

.section > * {
  margin: 12px;
}

.section-info {
  border-top: 1px solid #253747;
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  padding: 18px;
}

.pass-button {
  border-radius: 18px;
  padding: 11px;
}

.current-day {
  display: block;
  margin-bottom: 10px;
  font-size: 1.3rem;
}

.current-day-header {
  margin-bottom: 12px;
}

.current-day-info {
  font-size: 1rem;
}

.weather {
  float: right;
}

.diary-logo {
  max-width: 100px;
  margin-right: 18px;
}
</style>



// WEBPACK FOOTER //
// src/components/diary/DiaryEdit.vue